import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Check, Sparkles, ExternalLink, Crown, Copy, CheckCheck,
  ShieldAlert, FileText, AlertTriangle, Info, Mail,
} from "lucide-react";
import NavBar from "@/components/NavBar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "@/lib/i18n";
import { withCopecartLocale, appendQueryParams } from "@/lib/url";

// Phase 7.UI.15i — CopeCart cancellation deep-link. CopeCart's public
// buyer overview / cancellation request page. We open this in a new tab
// so the user keeps their Stockaily session.
const COPECART_CANCEL_URL_BASE = "https://copecart.com/buyer_order_overview_requests/new";

/**
 * Phase 7.UI.15i — Detect if the authenticated user has an active or
 * recently-active CopeCart subscription that must be cancelled via
 * CopeCart, not via Stockaily's internal "Switch to Free" action.
 *
 * Accepts BOTH spellings (legacy `copcart` + corrected `copecart`) of
 * `plan_source` and the order/subscription ID fields so it works for
 * users written by either era of the backend.
 */
function isCopecartManagedUser(u) {
  if (!u) return false;
  const src = (u.plan_source || "").toLowerCase();
  const provider = (u.billing_provider || "").toLowerCase();
  const status = (u.subscription_status || "").toLowerCase();
  const hasIds = !!(
    u.copcart_order_id || u.copecart_order_id
    || u.copcart_subscription_id || u.copecart_subscription_id
    || u.copcart_transaction_id || u.copecart_transaction_id
  );
  const isCopecartSource = src === "copcart" || src === "copecart";
  const isCopecartProvider = provider === "copecart" || provider === "copcart";
  const isPaid = (u.plan || "free").toLowerCase() !== "free";
  // A user qualifies if any source/provider/status signal indicates
  // CopeCart, OR they have a CopeCart order/subscription id on file
  // (paranoid override for users with a stale plan_source).
  if (isCopecartSource || isCopecartProvider) {
    return isPaid || status === "active" || status === "cancelled" || status === "past_due" || hasIds;
  }
  return false;
}

function fmtDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
  } catch {
    return null;
  }
}

function CopyChip({ value, copyLabel, copiedLabel, testId }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch { /* ignore */ }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId}
      className="ml-1.5 inline-flex items-center gap-1 rounded-md border border-border/60 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-card/70 transition-colors"
      aria-label={copied ? copiedLabel : copyLabel}
    >
      {copied ? <CheckCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      {copied ? copiedLabel : copyLabel}
    </button>
  );
}

function MetaRow({ label, value, testId, copyLabel, copiedLabel, allowCopy }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-border/30 last:border-b-0">
      <div className="text-xs text-muted-foreground shrink-0">{label}</div>
      <div className="text-sm font-mono text-right break-all flex items-center justify-end gap-1" data-testid={testId}>
        <span>{value}</span>
        {allowCopy ? <CopyChip value={value} copyLabel={copyLabel} copiedLabel={copiedLabel} testId={`${testId}-copy`} /> : null}
      </div>
    </div>
  );
}

function CopecartManagementCard({ user, t, locale, manageUrl }) {
  const isTest = !!user.latest_payment_test_mode;
  const status = (user.subscription_status || "").toLowerCase();
  const accessUntil = fmtDate(user.access_until);
  const nextRenewal = fmtDate(user.next_renewal_at || user.access_until);
  const lastPaymentDate = fmtDate(user.last_payment_at);
  const orderId = user.copcart_order_id || user.copecart_order_id || null;
  const transactionId = user.copcart_transaction_id || user.copecart_transaction_id || null;
  const subscriptionId = user.copcart_subscription_id || user.copecart_subscription_id || null;
  const purchaseEmail = user.copcart_customer_email || user.copecart_customer_email || user.email || null;
  const planLabel = (user.plan || "free").replace(/^./, (c) => c.toUpperCase());
  const intervalLabel = user.billing_interval
    ? user.billing_interval.replace(/^./, (c) => c.toUpperCase())
    : null;
  const lastAmount = (user.last_payment_amount != null)
    ? `${Number(user.last_payment_amount).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ${user.last_payment_currency || ""}`.trim()
    : null;

  const statusBadge = (() => {
    if (status === "active") {
      return <Badge variant="outline" className="border-emerald-500/40 text-emerald-700 dark:text-emerald-300 bg-emerald-500/10">{status}</Badge>;
    }
    if (status === "cancelled") {
      return <Badge variant="outline" className="border-amber-500/50 text-amber-700 dark:text-amber-300 bg-amber-500/10">{status}</Badge>;
    }
    if (status === "past_due") {
      return <Badge variant="outline" className="border-rose-500/50 text-rose-700 dark:text-rose-300 bg-rose-500/10">{status}</Badge>;
    }
    if (status) {
      return <Badge variant="outline" className="capitalize">{status}</Badge>;
    }
    return null;
  })();

  return (
    <section
      className="mt-6 rounded-2xl border border-border/70 bg-card/60 p-5 sm:p-6"
      data-testid="billing-copecart-management-card"
    >
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-display text-xl tracking-tight">{t("billing.copecart.title")}</h2>
            {isTest ? (
              <Badge
                variant="outline"
                className="border-amber-500/60 text-amber-700 dark:text-amber-300 bg-amber-500/10 uppercase tracking-wider text-[10px]"
                data-testid="billing-copecart-test-badge"
                title={t("billing.copecart.test_notice")}
              >
                {t("billing.copecart.test_badge")}
              </Badge>
            ) : null}
          </div>
          <p className="mt-1 text-sm text-muted-foreground max-w-prose">
            {t("billing.copecart.subtitle")}
          </p>
        </div>
      </div>

      {/* Subscription state notices */}
      {status === "cancelled" && accessUntil ? (
        <div
          className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-300"
          data-testid="billing-copecart-cancelled-notice"
        >
          {t("billing.copecart.cancelled_notice", { date: accessUntil })}
        </div>
      ) : null}
      {status === "past_due" ? (
        <div
          className="mt-4 rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-700 dark:text-rose-300 flex items-start gap-2"
          data-testid="billing-copecart-pastdue-notice"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{t("billing.copecart.past_due_notice")}</span>
        </div>
      ) : null}
      {isTest ? (
        <div
          className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300"
          data-testid="billing-copecart-test-notice"
        >
          {t("billing.copecart.test_notice")}
        </div>
      ) : null}

      {/* Meta grid */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div>
          <MetaRow
            label={t("billing.copecart.current_plan")}
            value={`${planLabel}${intervalLabel ? ` · ${intervalLabel}` : ""}`}
            testId="billing-copecart-current-plan"
          />
          <MetaRow
            label={t("billing.copecart.subscription_status")}
            value={statusBadge}
            testId="billing-copecart-status"
          />
          <MetaRow
            label={t("billing.copecart.access_until")}
            value={accessUntil}
            testId="billing-copecart-access-until"
          />
          <MetaRow
            label={t("billing.copecart.next_renewal")}
            value={status === "cancelled" ? null : nextRenewal}
            testId="billing-copecart-next-renewal"
          />
          <MetaRow
            label={t("billing.copecart.payment_provider")}
            value="CopeCart"
            testId="billing-copecart-provider"
          />
        </div>
        <div>
          <MetaRow
            label={t("billing.copecart.purchase_email")}
            value={purchaseEmail}
            testId="billing-copecart-purchase-email"
            allowCopy={!!purchaseEmail}
            copyLabel={t("billing.copecart.copy_label")}
            copiedLabel={t("billing.copecart.copied_label")}
          />
          <MetaRow
            label={t("billing.copecart.order_id")}
            value={orderId}
            testId="billing-copecart-order-id"
            allowCopy={!!orderId}
            copyLabel={t("billing.copecart.copy_label")}
            copiedLabel={t("billing.copecart.copied_label")}
          />
          <MetaRow
            label={t("billing.copecart.transaction_id")}
            value={transactionId}
            testId="billing-copecart-transaction-id"
            allowCopy={!!transactionId}
            copyLabel={t("billing.copecart.copy_label")}
            copiedLabel={t("billing.copecart.copied_label")}
          />
          <MetaRow
            label={t("billing.copecart.subscription_id")}
            value={subscriptionId}
            testId="billing-copecart-subscription-id"
            allowCopy={!!subscriptionId}
            copyLabel={t("billing.copecart.copy_label")}
            copiedLabel={t("billing.copecart.copied_label")}
          />
        </div>
      </div>

      {/* Cancel CTA */}
      <div className="mt-5 flex flex-col gap-3">
        <Button
          type="button"
          variant="outline"
          className="self-start"
          data-testid="billing-copecart-cancel-cta"
          onClick={() => window.open(
            withCopecartLocale(manageUrl || COPECART_CANCEL_URL_BASE, locale),
            "_blank",
            "noopener,noreferrer",
          )}
        >
          {t("billing.copecart.cancel_button")}
          <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
        </Button>
        <p className="text-xs text-muted-foreground max-w-prose leading-relaxed">
          {t("billing.copecart.cancel_helper")}
        </p>
        <div
          className="rounded-md border border-border/60 bg-card/40 p-3 text-xs text-muted-foreground flex items-start gap-2 max-w-prose"
          data-testid="billing-copecart-safety-copy"
        >
          <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
          <span>{t("billing.copecart.cancel_safety")}</span>
        </div>
      </div>

      {/* Invoices block */}
      <div className="mt-5 rounded-lg border border-border/60 bg-card/30 p-3" data-testid="billing-copecart-invoices">
        <div className="flex items-start gap-2">
          <FileText className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
          <div className="flex-1">
            <div className="text-sm font-medium">{t("billing.copecart.invoices_title")}</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-prose">
              {t("billing.copecart.invoices_note")}
            </p>
            {(lastPaymentDate || lastAmount || orderId) ? (
              <div className="mt-2 text-xs font-mono text-muted-foreground">
                {t("billing.copecart.latest_payment")}:{" "}
                {[lastPaymentDate, lastAmount, orderId ? `#${orderId}` : null]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Billing() {
  const { user, loading } = useAuth();
  const { t, locale } = useTranslation();
  const [usage, setUsage] = useState(null);
  const [plans, setPlans] = useState([]);
  // Phase 7.UI.15j — checkout_enabled gates which disclaimer copy we
  // show. Defaults to false (the safer copy) until /billing/plans loads.
  const [checkoutEnabled, setCheckoutEnabled] = useState(false);
  // Phase 7.UI.15l — backend-provided locale-aware CopeCart management URL.
  const [manageUrl, setManageUrl] = useState(null);
  // Billing Cancellation Flow Repair — the /api/billing/subscription/
  // manage-link response is the single source of truth for what the
  // "Manage subscription" and "Switch to Free" buttons actually do.
  const [manageInfo, setManageInfo] = useState(null);
  const [manageInfoLoading, setManageInfoLoading] = useState(true);
  const [manageInfoError, setManageInfoError] = useState(null);
  // Which confirmation dialog is currently open (null = none).
  const [confirmDialog, setConfirmDialog] = useState(null); // "copecart" | "support" | "error" | null
  const [interval, setInterval] = useState("yearly");
  const [params] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login?next=/billing"); return; }
    const qInterval = params.get("interval");
    if (qInterval && ["monthly", "yearly"].includes(qInterval)) setInterval(qInterval);
    let cancelled = false;
    api.get("/usage")
      .then((r) => { if (!cancelled) setUsage(r.data); })
      .catch(() => { /* noop */ });
    api.get(`/billing/plans?locale=${encodeURIComponent(locale || "en")}`)
      .then((r) => {
        if (cancelled) return;
        setPlans(r.data?.plans || []);
        // Phase 7.UI.15j — checkout_enabled may be undefined on older
        // backends; default to false so the safer disclaimer stays.
        setCheckoutEnabled(!!r.data?.checkout_enabled);
        // Phase 7.UI.15l — locale-aware CopeCart management URL.
        if (r.data?.manage_subscription_url) {
          setManageUrl(r.data.manage_subscription_url);
        }
      })
      .catch(() => { /* noop */ });
    // Billing Cancellation Flow Repair — classify the user's plan
    // source and resolve the correct action for the "Switch to Free"
    // button.  The URL is server-generated; the frontend never
    // constructs a CopeCart URL from raw IDs.
    setManageInfoLoading(true);
    setManageInfoError(null);
    api.get(`/billing/subscription/manage-link?locale=${encodeURIComponent(locale || "en")}`)
      .then((r) => {
        if (cancelled) return;
        setManageInfo(r.data || null);
        setManageInfoLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setManageInfo(null);
        setManageInfoError(e?.response?.status || "network_error");
        setManageInfoLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  const onCheckout = async (plan) => {
    try {
      // Phase 7.UI.15j — pass current locale so the CopeCart checkout
      // opens in the same language as the Stockaily UI. The legacy
      // /billing/checkout endpoint returns a placeholder URL; we still
      // append locale defensively in case it changes.
      const res = await api.post("/billing/checkout", { plan, interval });
      if (res.data?.checkout_url) {
        toast.message(t("billing.redirect_msg"));
        const finalUrl = appendQueryParams(res.data.checkout_url, { locale: locale || "en" });
        window.open(finalUrl, "_blank");
      }
    } catch {
      toast.error(t("billing.checkout_failed"));
    }
  };

  // Billing Cancellation Flow Repair — safe, source-aware handler for
  // the "Switch to Free" button.  It never mutates the local plan
  // directly.  The eventual downgrade is driven exclusively by
  // CopeCart webhook state (cancelled / expired) for CopeCart users
  // and by operator support for manual/admin/promo users.
  const onSwitchFree = async () => {
    // If we don't yet have the classification response, re-fetch —
    // never no-op silently.
    if (manageInfoLoading) {
      toast.message(t("billing.manage.loading"));
      return;
    }
    let info = manageInfo;
    if (!info) {
      try {
        const r = await api.get(
          `/billing/subscription/manage-link?locale=${encodeURIComponent(locale || "en")}`
        );
        info = r.data;
        setManageInfo(info);
      } catch {
        setConfirmDialog("error");
        return;
      }
    }
    const action = info?.action;
    if (action === "already_free") {
      toast.message(t("billing.manage.already_free_toast"));
      return;
    }
    if (action === "open_copecart_manage") {
      setConfirmDialog("copecart");
      return;
    }
    if (action === "contact_support") {
      setConfirmDialog("support");
      return;
    }
    // error / unknown
    setConfirmDialog("error");
  };

  const openCopecartManage = () => {
    const url = manageInfo?.manage_url || manageUrl;
    if (!url) {
      setConfirmDialog("error");
      return;
    }
    try {
      window.open(
        withCopecartLocale(url, locale),
        "_blank",
        "noopener,noreferrer",
      );
      setConfirmDialog(null);
    } catch {
      setConfirmDialog("error");
    }
  };

  const openSupportEmail = () => {
    const email = manageInfo?.support_email || "support@stockaily.ai";
    try {
      window.location.href = `mailto:${email}?subject=${encodeURIComponent(
        "Stockaily: plan change / cancellation request",
      )}`;
      setConfirmDialog(null);
    } catch {
      setConfirmDialog(null);
    }
  };

  const remainingPct = (() => {
    if (!usage || usage.unlimited) return 100;
    if (!usage.limit) return 0;
    return Math.max(0, Math.min(100, Math.round((usage.remaining / usage.limit) * 100)));
  })();

  const savingsPct = useMemo(
    () => plans.find((p) => p.key === "pro")?.yearly_savings_pct || 17,
    [plans]
  );

  // Lookup helper for backend-served feature strings.
  const tFeatureItem = (item) => {
    const translated = t(`pricing.features.${item}`);
    return translated === `pricing.features.${item}` ? item : translated;
  };

  // Phase 7.UI.15i — detect CopeCart-paid users so we can show the
  // safe management card AND disable the unsafe internal "Switch to
  // Free" cancellation pathway.
  const isCopecart = isCopecartManagedUser(user);

  return (
    <div className="min-h-screen flex flex-col">
      <NavBar showSearch={false} />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12" data-testid="billing-page">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl tracking-tight">{t("billing.title")}</h1>
            <p className="text-muted-foreground mt-1">{t("billing.subtitle")}</p>
          </div>
          {user ? (
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge variant="outline" className="uppercase tabular">{t("billing.plan_label")}: {user.plan || "free"}</Badge>
              {user.billing_interval ? (
                <Badge variant="outline" className="capitalize">{t("billing.billed_label")}: {user.billing_interval}</Badge>
              ) : null}
              {isCopecart && user.latest_payment_test_mode ? (
                <Badge
                  variant="outline"
                  className="uppercase tracking-wider text-[10px] border-amber-500/60 text-amber-700 dark:text-amber-300 bg-amber-500/10"
                  data-testid="billing-header-test-badge"
                >
                  {t("billing.copecart.test_badge")}
                </Badge>
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Phase 7.UI.15i — CopeCart subscription management card sits
            ABOVE the usage block + plan cards so it's the first thing a
            paid user sees on their billing page. */}
        {isCopecart ? <CopecartManagementCard user={user} t={t} locale={locale} manageUrl={manageUrl} /> : null}

        <section className="mt-6 rounded-2xl border border-border/70 bg-card/60 p-5 sm:p-6" data-testid="billing-usage-card">
          <div className="text-sm text-muted-foreground">{t("billing.analyses_used")}</div>
          {!usage ? (
            <div className="mt-2">{t("common.loading")}</div>
          ) : usage.unlimited ? (
            <div className="mt-2 font-display text-2xl">{t("billing.unlimited_msg", { plan: usage.plan })}</div>
          ) : (
            <>
              <div className="mt-2 font-display text-2xl tabular">
                {usage.phase === "free_first_30d"
                  ? t("billing.used_in_first", { used: usage.used, limit: usage.limit })
                  : t("billing.used_in_rolling", { used: usage.used, limit: usage.limit, days: usage.window_days })}
              </div>
              <Progress value={100 - remainingPct} className="mt-3" data-testid="billing-usage-progress" />
              <div className="mt-1 text-xs text-muted-foreground">{t("billing.remaining", { remaining: usage.remaining })}</div>
            </>
          )}
        </section>

        {/* Interval toggle */}
        <div className="mt-8 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-display text-xl">{t("billing.choose_plan")}</div>
          <div className="inline-flex items-center rounded-full border border-border/70 bg-card/60 p-1 shadow-[var(--app-shadow-elev-1)]">
            <IntervalButton active={interval === "monthly"} onClick={() => setInterval("monthly")} testId="billing-toggle-monthly">{t("pricing.monthly")}</IntervalButton>
            <IntervalButton active={interval === "yearly"} onClick={() => setInterval("yearly")} testId="billing-toggle-yearly">
              {t("pricing.yearly")}
              <span
                className="ml-2 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider"
                style={{
                  color: "#3FAE89",
                  background: "rgba(63,174,137,0.14)",
                  border: "1px solid rgba(63,174,137,0.34)",
                }}
              >
                {t("common.save_pct", { pct: savingsPct })}
              </span>
            </IntervalButton>
          </div>
        </div>

        <section className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {plans.map((p) => {
            const isCurrent = (user?.plan || "free") === p.key;
            const recommended = p.key === "pro";
            const isPremium = !!p.premium_tier;
            const monthly = p.monthly_price_eur || 0;
            const yearly = p.yearly_price_eur || 0;
            const yearlyMonthly = p.yearly_monthly_equivalent_eur || (yearly / 12);
            const showPrice = p.key === "free" ? 0 : (interval === "yearly" ? yearlyMonthly : monthly);
            const accent = isPremium
              ? { fg: "#A47864", border: "rgba(164,120,100,0.45)" }
              : recommended
                ? { fg: "#3182CE", border: "rgba(49,130,206,0.55)" }
                : { fg: "#7D8592", border: "rgba(125,133,146,0.32)" };
            // Phase 7.UI.15i — disable the "Switch to Free" pathway for
            // CopeCart-paid users. They must cancel via CopeCart so we
            // never silently downgrade an actively-billed subscription.
            const switchFreeBlocked = p.key === "free" && isCopecart && !isCurrent;
            return (
              <div
                key={p.key}
                style={{ borderColor: accent.border }}
                className={`relative rounded-2xl border p-6 bg-card/60 ${recommended ? "shadow-[0_0_60px_-20px_rgba(49,130,206,0.4)]" : isPremium ? "shadow-[0_0_60px_-22px_rgba(164,120,100,0.36)]" : ""}`}
                data-testid="billing-plan-card"
                data-plan={p.key}
              >
                {recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-0.5 text-[11px]" style={{ borderColor: accent.border, color: accent.fg }}>
                    <Sparkles className="w-3 h-3" /> {t("billing.recommended")}
                  </div>
                )}
                {isPremium && !recommended && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full border bg-card px-2.5 py-0.5 text-[11px]" style={{ borderColor: accent.border, color: accent.fg }}>
                    <Crown className="w-3 h-3" /> {t("common.premium_label")}
                  </div>
                )}
                <div className="flex items-baseline gap-2">
                  <div className="font-display text-xl">{p.name}</div>
                  {isCurrent ? <Badge variant="outline" className="text-[10px]">{t("common.current")}</Badge> : null}
                </div>
                <div className="mt-1 font-display text-3xl tabular" style={{ color: accent.fg }}>
                  {p.key === "free" ? "\u20AC0" : `\u20AC${(showPrice).toFixed(showPrice < 10 || !Number.isInteger(showPrice) ? 2 : 0)}`}
                  <span className="text-sm text-muted-foreground font-sans"> {t("common.per_month_short")}</span>
                </div>
                {p.key !== "free" && interval === "yearly" ? (
                  <div className="text-[11px] text-muted-foreground tabular mt-0.5">{t("common.billed_yearly_short", { amount: yearly, pct: p.yearly_savings_pct })}</div>
                ) : p.key !== "free" ? (
                  <div className="text-[11px] text-muted-foreground tabular mt-0.5">{t("common.or_yearly_short", { amount: yearly, pct: p.yearly_savings_pct })}</div>
                ) : null}

                <ul className="mt-4 space-y-1.5 text-sm">
                  {(p.feature_groups?.[0]?.items || p.features || []).slice(0, 6).map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: accent.fg }} />
                      <span className="leading-snug">{tFeatureItem(f)}</span>
                    </li>
                  ))}
                </ul>

                {p.key === "free" ? (
                  <div className="mt-5">
                    {switchFreeBlocked ? (
                      // Active CopeCart subscription: this used to be a
                      // disabled "Switch to Free" no-op, which was
                      // confusing.  The button is now actionable — it
                      // opens the same confirmation + backend-resolved
                      // CopeCart management flow as "Manage or cancel
                      // subscription".  The local plan is never
                      // mutated here; the downgrade waits for the
                      // CopeCart cancelled / expired webhook.
                      <>
                        <Button
                          className="w-full"
                          variant="default"
                          disabled={manageInfoLoading}
                          data-testid="billing-manage-cancel-copecart"
                          onClick={onSwitchFree}
                        >
                          {manageInfoLoading
                            ? t("billing.manage.loading")
                            : (
                              <>
                                {t("billing.manage.cta_copecart")}
                                <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                              </>
                            )}
                        </Button>
                        <div
                          className="mt-2 rounded-md border border-border/60 bg-card/40 p-2 text-[11px] text-muted-foreground flex items-start gap-1.5"
                          data-testid="billing-copecart-cancel-help"
                        >
                          <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                          <div>
                            <div className="font-medium text-foreground">{t("billing.copecart.switch_free_disabled_title")}</div>
                            <div className="mt-0.5 leading-relaxed">{t("billing.copecart.switch_free_disabled_body")}</div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <Button
                        className="w-full"
                        variant="outline"
                        disabled={isCurrent || manageInfoLoading}
                        data-testid="billing-switch-free"
                        onClick={isCurrent ? undefined : onSwitchFree}
                      >
                        {isCurrent
                          ? t("common.current_plan")
                          : (manageInfoLoading
                            ? t("billing.manage.loading")
                            : t("billing.switch_free"))}
                      </Button>
                    )}
                  </div>
                ) : (
                  <Button className="mt-5 w-full" onClick={() => onCheckout(p.key)} data-testid={`billing-checkout-${p.key}`} disabled={isCurrent}>
                    {isCurrent ? t("common.current_plan") : (
                      <>{interval === "yearly" ? t("billing.checkout_yearly") : t("billing.checkout_monthly")} <ExternalLink className="w-3.5 h-3.5 ml-1" /></>
                    )}
                  </Button>
                )}
              </div>
            );
          })}
        </section>

        <section className="mt-8 rounded-2xl border border-border/70 bg-card/30 p-5 text-sm text-muted-foreground" data-testid="billing-disclaimer">
          {checkoutEnabled ? t("billing.disclaimer_enabled") : t("billing.disclaimer_disabled")}
        </section>

        {/* Billing Cancellation Flow Repair — restored explanatory
            "How your subscription works" section. Renders for every
            user (free, CopeCart, manual/admin/promo) so the Billing
            page ALWAYS shows: current plan, billing interval, usage
            allowance, where billing is processed and how cancellation
            works.  Never fabricates numbers — every value comes from
            /usage or /billing/subscription/manage-link. */}
        <HowItWorksSection
          t={t}
          user={user}
          usage={usage}
          info={manageInfo}
          infoLoading={manageInfoLoading}
          infoError={manageInfoError}
        />
      </main>

      {/* CopeCart cancellation confirmation dialog */}
      <AlertDialog
        open={confirmDialog === "copecart"}
        onOpenChange={(o) => { if (!o) setConfirmDialog(null); }}
      >
        <AlertDialogContent data-testid="billing-confirm-copecart-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("billing.manage.confirm_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("billing.manage.confirm_dialog_body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-confirm-copecart-cancel">
              {t("billing.manage.confirm_dialog_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-confirm-copecart-confirm"
              onClick={openCopecartManage}
            >
              {t("billing.manage.confirm_dialog_confirm")}
              <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual/admin/promo contact-support dialog */}
      <AlertDialog
        open={confirmDialog === "support"}
        onOpenChange={(o) => { if (!o) setConfirmDialog(null); }}
      >
        <AlertDialogContent data-testid="billing-confirm-support-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("billing.manage.support_dialog_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("billing.manage.support_dialog_body", {
                email: manageInfo?.support_email || "support@stockaily.ai",
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-confirm-support-cancel">
              {t("billing.manage.support_dialog_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-confirm-support-confirm"
              onClick={openSupportEmail}
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              {t("billing.manage.support_dialog_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Error dialog — no button silently no-ops */}
      <AlertDialog
        open={confirmDialog === "error"}
        onOpenChange={(o) => { if (!o) setConfirmDialog(null); }}
      >
        <AlertDialogContent data-testid="billing-confirm-error-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {manageInfo?.error_code === "manage_link_not_configured"
                ? t("billing.manage.error_not_configured_title")
                : t("billing.manage.error_generic_title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {manageInfo?.error_code === "manage_link_not_configured"
                ? t("billing.manage.error_not_configured_body", {
                    email: manageInfo?.support_email || "support@stockaily.ai",
                  })
                : t("billing.manage.error_generic_body", {
                    email: manageInfo?.support_email || "support@stockaily.ai",
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="billing-confirm-error-close">
              {t("billing.manage.support_dialog_cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="billing-confirm-error-support"
              onClick={openSupportEmail}
            >
              <Mail className="w-3.5 h-3.5 mr-1.5" />
              {t("billing.manage.support_dialog_confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Footer />
    </div>
  );
}

// -------- Restored "How your subscription works" section -----------
function HowItWorksSection({ t, user, usage, info, infoLoading, infoError }) {
  const plan = (user?.plan || "free").toLowerCase();
  const planLabel = plan.replace(/^./, (c) => c.toUpperCase());
  const billingInterval = user?.billing_interval || null;
  const intervalLabel = billingInterval
    ? billingInterval.replace(/^./, (c) => c.toUpperCase())
    : t("billing.how_it_works.billing_interval_none");

  const allowanceValue = (() => {
    if (!usage) return "\u2014";
    if (usage.unlimited) return t("billing.how_it_works.allowance_unlimited");
    if (usage.phase === "free_first_30d") {
      return t("billing.how_it_works.allowance_free_first_30d", {
        used: usage.used, limit: usage.limit,
      });
    }
    return t("billing.how_it_works.allowance_count", {
      used: usage.used, limit: usage.limit, remaining: usage.remaining,
    });
  })();

  // Resolve the "Billed by" and "Cancellation" copy purely from the
  // backend-classified plan_source.  Falls back gracefully while the
  // classification is still loading.
  const source = (info?.plan_source || "").toLowerCase();
  const billedBy = (() => {
    if (source === "copecart") return t("billing.how_it_works.billed_by_copecart");
    if (source === "manual") return t("billing.how_it_works.billed_by_manual");
    if (source === "admin") return t("billing.how_it_works.billed_by_admin");
    if (source === "promo") return t("billing.how_it_works.billed_by_promo");
    if (plan === "free") return t("billing.how_it_works.billed_by_none");
    return "\u2014";
  })();
  const supportEmail = info?.support_email || "support@stockaily.ai";
  const cancellation = (() => {
    if (source === "copecart") return t("billing.how_it_works.cancellation_copecart");
    if (source === "manual" || source === "admin" || source === "promo") {
      return t("billing.how_it_works.cancellation_manual", { email: supportEmail });
    }
    return t("billing.how_it_works.cancellation_free");
  })();

  const accessUntil = (info?.access_until || user?.access_until) || null;
  const accessUntilStr = accessUntil ? fmtDate(accessUntil) : null;

  return (
    <section
      className="mt-6 rounded-2xl border border-border/70 bg-card/50 p-5 sm:p-6"
      data-testid="billing-how-it-works"
    >
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
        <div className="flex-1">
          <h2 className="font-display text-lg tracking-tight">
            {t("billing.how_it_works.title")}
          </h2>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 text-sm">
            <div className="border-b border-border/30 py-1.5 flex justify-between gap-3">
              <dt className="text-xs text-muted-foreground">
                {t("billing.how_it_works.current_plan_row")}
              </dt>
              <dd className="font-mono text-right" data-testid="billing-how-current-plan">
                {planLabel}
              </dd>
            </div>
            <div className="border-b border-border/30 py-1.5 flex justify-between gap-3">
              <dt className="text-xs text-muted-foreground">
                {t("billing.how_it_works.billing_interval_row")}
              </dt>
              <dd className="font-mono text-right" data-testid="billing-how-interval">
                {intervalLabel}
              </dd>
            </div>
            <div className="border-b border-border/30 py-1.5 flex justify-between gap-3">
              <dt className="text-xs text-muted-foreground">
                {t("billing.how_it_works.allowance_row")}
              </dt>
              <dd className="text-right" data-testid="billing-how-allowance">
                {allowanceValue}
              </dd>
            </div>
            <div className="border-b border-border/30 py-1.5 flex justify-between gap-3">
              <dt className="text-xs text-muted-foreground">
                {t("billing.how_it_works.billed_by_row")}
              </dt>
              <dd className="text-right" data-testid="billing-how-billed-by">
                {infoLoading ? t("billing.manage.loading") : billedBy}
              </dd>
            </div>
            {accessUntilStr ? (
              <div className="border-b border-border/30 py-1.5 flex justify-between gap-3 sm:col-span-2">
                <dt className="text-xs text-muted-foreground">
                  {t("billing.how_it_works.access_until_row")}
                </dt>
                <dd className="text-right" data-testid="billing-how-access-until">
                  {accessUntilStr}
                </dd>
              </div>
            ) : null}
            <div className="py-1.5 flex justify-between gap-3 sm:col-span-2">
              <dt className="text-xs text-muted-foreground shrink-0">
                {t("billing.how_it_works.cancellation_row")}
              </dt>
              <dd className="text-right leading-snug max-w-md" data-testid="billing-how-cancellation">
                {infoLoading
                  ? t("billing.manage.loading")
                  : (infoError
                    ? t("billing.how_it_works.cancellation_free")
                    : cancellation)}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
}

function IntervalButton({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${active ? "bg-card text-foreground shadow-[var(--app-shadow-elev-1)]" : "text-muted-foreground hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}
